import { IconButton, Typography } from '@mui/material';
import React, { Dispatch, SetStateAction, useCallback, useContext, useMemo, useState } from 'react';
import { convertBytesToMbsOrKbs } from '../../../common/attachments/attachmentsApi';
import { SECURITY_ACTIONS } from '../../../common/auth/Security';
import type { SecuredActionType } from '../../../common/components/ActionType';
import EllipsisText from '../../../common/components/EllipsisText';
import Loading from '../../../common/components/Loading';
import ResponsiveActions from '../../../common/components/ResponsiveActions';
import { ENTITY_CONSTANTS } from '../../../common/constants/entityConstants';
import { FormContext } from '../../../common/form/FormContext';
import I18n from '../../../common/i18n/I18n';
import { Icons } from '../../../common/icons/Icons';
import { H, V } from '../../../common/Layout';
import { useStyling } from '../../../common/Theme';
import RoundAvatar, { AvatarSizes } from '../../../production/header/RoundAvatar';
import { BasicInfoInputExtended } from '../detailsPageUtilities/personBasicInformation';
import { useRemoveResume } from './annotationApi';

export default function ResumeRenderer({
    selectedFile = null,
    onSuccess,
    setState
}: {
    selectedFile: File | null;
    onSuccess: () => void;
    setState: Dispatch<SetStateAction<BasicInfoInputExtended>>;
}) {
    const { theme, isDarkMode } = useStyling();
    const [isHover, setIsHover] = useState(false);
    const { onDeleteResume, removingResume } = useRemoveResume();
    const { state } = useContext(FormContext);

    const handleDeleteResume = useCallback(() => {
        onDeleteResume().then(() => {
            setState((prevData) => ({ ...prevData, resumeReference: undefined }));
            onSuccess?.();
        });
    }, [onDeleteResume, onSuccess, setState]);

    const securedActions = useMemo<SecuredActionType[]>(
        () => [
            {
                securityAction: SECURITY_ACTIONS.DELETE,
                entityName: ENTITY_CONSTANTS.person,
                icon: Icons.Delete,
                label: <I18n token="actions.delete" />,
                onClick: handleDeleteResume,
                disabled: removingResume
            }
        ],
        [handleDeleteResume, removingResume]
    );

    return (
        <H
            fill
            sx={{
                p: 1,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                '&:hover': {
                    backgroundColor: theme.palette.grey[isDarkMode ? 700 : 100]
                }
            }}
        >
            <H
                fill
                sx={{ gap: 2, flexGrow: 1, position: 'relative', cursor: 'pointer' }}
                onMouseEnter={() => setIsHover(true)}
                onMouseLeave={() => setIsHover(false)}
            >
                <RoundAvatar
                    size={AvatarSizes.md}
                    title={selectedFile?.name || state?.resumeReference?.label}
                    bgColor={theme.palette.background.default}
                />
                <V sx={{ flexGrow: 1 }}>
                    <EllipsisText text={selectedFile?.name || state?.resumeReference?.label} />
                    <H sx={{ gap: 1, mt: 1, width: 'fit-content' }}>
                        {selectedFile?.size && (
                            <Typography fontSize={'x-small'} fontWeight={'normal'} color={theme.palette.text.disabled}>
                                {convertBytesToMbsOrKbs(selectedFile?.size)}
                            </Typography>
                        )}
                        {selectedFile?.type && (
                            <Typography fontSize={'x-small'} fontWeight={'normal'} color={theme.palette.text.disabled}>
                                {selectedFile?.type}
                            </Typography>
                        )}

                        <Typography
                            fontSize={'x-small'}
                            fontWeight={'normal'}
                            className={'ellipsis'}
                            sx={{ flexGrow: 2 }}
                            color={theme.palette.text.disabled}
                        >
                            .{selectedFile?.name.split('.').pop() || state?.resumeReference?.label.split('.').pop()}
                        </Typography>
                    </H>
                </V>

                <H
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                    sx={{ justifyContent: 'flex-end' }}
                >
                    {removingResume ? (
                        <IconButton sx={{ borderRadius: '18px' }}>
                            <Loading size="24px" center />
                        </IconButton>
                    ) : (
                        <ResponsiveActions
                            securedActions={securedActions}
                            showBorder
                            isActivated={isHover}
                        />
                    )}
                </H>
            </H>
        </H>
    );
}
